/**
 * Webhook Delivery Log Hooks (Sprint C14 Lane B WP B-4)
 *
 * React Query wrappers around the webhook-delivery-log API service.
 *
 * Polling cadence: 10s on the list and health endpoints. The detail
 * endpoint is fetched on-demand (drill-down panel) and not polled.
 */

import { useQuery } from '@tanstack/react-query';
import {
  webhookDeliveryLogApi,
  type WebhookDeliveryListQuery,
  type WebhookDeliveryListResponse,
  type WebhookDeliveryDetail,
  type WebhookHealthMetrics,
} from '../api/services';

const POLL_INTERVAL_MS = 10_000;

export function useWebhookDeliveries(query: WebhookDeliveryListQuery = {}) {
  return useQuery<WebhookDeliveryListResponse>({
    queryKey: ['admin', 'webhook-deliveries', 'list', query],
    queryFn: () => webhookDeliveryLogApi.list(query),
    refetchInterval: POLL_INTERVAL_MS,
    refetchOnWindowFocus: false,
  });
}

export function useWebhookDeliveryHealth() {
  return useQuery<WebhookHealthMetrics>({
    queryKey: ['admin', 'webhook-deliveries', 'health'],
    queryFn: () => webhookDeliveryLogApi.getHealth(),
    refetchInterval: POLL_INTERVAL_MS,
    refetchOnWindowFocus: false,
  });
}

export function useWebhookDeliveryDetail(id: string | null) {
  return useQuery<WebhookDeliveryDetail>({
    queryKey: ['admin', 'webhook-deliveries', 'detail', id],
    queryFn: () => {
      if (id === null) {
        throw new Error('id is required');
      }
      return webhookDeliveryLogApi.getById(id);
    },
    enabled: id !== null,
    refetchOnWindowFocus: false,
  });
}
